import React, { useState } from 'react';
import {
  Box,
  Typography,
  Slider,
  Grid,
  Paper,
  Chip,
  Button,
  FormControlLabel,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon,
  Speed as SpeedIcon,
  TuneRounded as TuneIcon,
} from '@mui/icons-material';

interface DensitySlidersProps {
  settings: {
    targetKeywordDensity: number;
    targetEntityDensity: number;
    targetCQCScore: number;
    targetReadability: number;
    autoAddQuestions: boolean;
    naturalLanguage: boolean;
    matchCompetitor: boolean;
  };
  onChange: (settings: any) => void;
  currentAnalysis: any;
  onApply: (settings: any) => void;
}

export const DensitySliders: React.FC<DensitySlidersProps> = ({
  settings,
  onChange,
  currentAnalysis,
  onApply
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  const handleSliderChange = (key: string, value: number) => {
    const newSettings = { ...localSettings, [key]: value };
    setLocalSettings(newSettings);
    onChange(newSettings);
  };

  const handleSwitchChange = (key: string, checked: boolean) => {
    const newSettings = { ...localSettings, [key]: checked };
    setLocalSettings(newSettings);
    onChange(newSettings);
  };

  const applyOptimalSettings = () => {
    const optimal = {
      ...localSettings,
      targetKeywordDensity: 2.5,
      targetEntityDensity: 4.0,
      targetCQCScore: 85,
      targetReadability: 65,
      autoAddQuestions: true,
      naturalLanguage: true,
    };
    setLocalSettings(optimal);
    onChange(optimal);
  };

  const getSliderColor = (current: number, target: number, optimal: [number, number]) => {
    if (target >= optimal[0] && target <= optimal[1]) return 'primary';
    return 'warning';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Optimization Controls</Typography>
          {localSettings.matchCompetitor && (
            <Chip
              label="📊 Matching Top Competitor"
              color="success"
              size="small"
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TuneIcon />}
            onClick={applyOptimalSettings}
          >
            Apply Optimal
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<MagicIcon />}
            onClick={() => onApply(localSettings)}
            sx={{
              background: 'linear-gradient(45deg, #9c27b0 30%, #673ab7 90%)',
            }}
          >
            Apply Settings
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Keyword Density Slider */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Keyword Density</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Current: ${currentAnalysis?.keywordDensity?.toFixed(1) || 0}%`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Target: ${localSettings.targetKeywordDensity}%`}
                  size="small"
                  color="primary"
                />
              </Box>
            </Box>
            
            <Slider
              value={localSettings.targetKeywordDensity}
              onChange={(_, value) => handleSliderChange('targetKeywordDensity', value as number)}
              min={0.5}
              max={5}
              step={0.1}
              marks={[
                { value: 0.5, label: '0.5%' },
                { value: 2.5, label: 'Optimal' },
                { value: 5, label: '5%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              color={getSliderColor(currentAnalysis?.keywordDensity || 0, localSettings.targetKeywordDensity, [2, 3.5])}
            />
            
            <Typography variant="caption" color="textSecondary">
              Optimal range: 2-3.5% for natural integration
            </Typography>
          </Paper>
        </Grid>

        {/* Entity Density Slider */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Entity Density</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Current: ${currentAnalysis?.entityDensity?.toFixed(1) || 0}/100w`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Target: ${localSettings.targetEntityDensity}/100w`}
                  size="small"
                  color="primary"
                />
              </Box>
            </Box>
            
            <Slider
              value={localSettings.targetEntityDensity}
              onChange={(_, value) => handleSliderChange('targetEntityDensity', value as number)}
              min={1}
              max={8}
              step={0.5}
              marks={[
                { value: 1, label: '1' },
                { value: 4, label: 'Optimal' },
                { value: 8, label: '8' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}/100w`}
              color={getSliderColor(currentAnalysis?.entityDensity || 0, localSettings.targetEntityDensity, [3, 5])}
            />
            
            <Typography variant="caption" color="textSecondary">
              Semantic entities per 100 words (places, people, things)
            </Typography>
          </Paper>
        </Grid>

        {/* CQC Score Target */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Target CQC Score</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Current: ${currentAnalysis?.cqcScore || 0}%`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Target: ${localSettings.targetCQCScore}%`}
                  size="small"
                  color="success"
                />
              </Box>
            </Box>
            
            <Slider
              value={localSettings.targetCQCScore}
              onChange={(_, value) => handleSliderChange('targetCQCScore', value as number)}
              min={50}
              max={100}
              step={5}
              marks={[
                { value: 50, label: '50%' },
                { value: 85, label: 'Optimal' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}%`}
              color="success"
            />
            
            <Typography variant="caption" color="textSecondary">
              Customer Question Coverage - aim for 80%+ for best results
            </Typography>
          </Paper>
        </Grid>

        {/* Readability Score */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Readability Target</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Current: ${currentAnalysis?.readabilityScore || 0}`}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={`Target: ${localSettings.targetReadability}`}
                  size="small"
                  color="info"
                />
              </Box>
            </Box>
            
            <Slider
              value={localSettings.targetReadability}
              onChange={(_, value) => handleSliderChange('targetReadability', value as number)}
              min={30}
              max={90}
              step={5}
              marks={[
                { value: 30, label: 'Complex' },
                { value: 60, label: 'Optimal' },
                { value: 90, label: 'Simple' },
              ]}
              valueLabelDisplay="auto"
              color="info"
            />
            
            <Typography variant="caption" color="textSecondary">
              Flesch Reading Ease - 60-70 is ideal for web content
            </Typography>
          </Paper>
        </Grid>

        {/* Advanced Options */}
        <Grid size={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Advanced Options</Typography>
            
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.autoAddQuestions}
                      onChange={(e) => handleSwitchChange('autoAddQuestions', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Auto-Add Questions</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Naturally weave in missing PAA questions
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.naturalLanguage}
                      onChange={(e) => handleSwitchChange('naturalLanguage', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Natural Language</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Maintain conversational tone
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={localSettings.matchCompetitor}
                      onChange={(e) => handleSwitchChange('matchCompetitor', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Match Top Competitor</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Optimize to match #1 ranking page
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Real-time Preview */}
      <Paper sx={{ mt: 3, p: 2, bgcolor: 'primary.light' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Optimization Preview
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption">Keywords to add:</Typography>
            <Typography variant="h6">
              ~{Math.round((localSettings.targetKeywordDensity - (currentAnalysis?.keywordDensity || 0)) * 10)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption">Entities needed:</Typography>
            <Typography variant="h6">
              ~{Math.round((localSettings.targetEntityDensity - (currentAnalysis?.entityDensity || 0)) * 5)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption">Questions to cover:</Typography>
            <Typography variant="h6">
              {Math.max(0, Math.round((localSettings.targetCQCScore - (currentAnalysis?.cqcScore || 0)) / 10))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="caption">Est. AI Overview:</Typography>
            <Typography variant="h6">
              {Math.min(95, Math.round(localSettings.targetCQCScore * 0.8))}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};